import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { Answer, Course, Formation, Question, Test } from "types";
import type { RootState } from "../store";

// Define a type for the slice state
interface UpdateFormationState {
  title: string;
  description: string;
  price: string;
  image: any;
  loading: boolean;
  error: string;
  test: Test;
  courses: Course[];
  testQuestions: Question[];
  testQuestionsAnswers: Answer[];
  formationToUpdate: Formation;
  questionToUpdate: Question;
  answerToUpdate: Answer;
  courseToUpdate: Course;
}

// Define the initial state using that type
const initialState: UpdateFormationState = {
  formationToUpdate: null,
  description: "",
  price: "",
  title: "",
  image: "",
  loading: true,
  error: "",
  courses: [],
  test: {
    addedOn: null,
    description: "",
    formationId: 0,
    id: 0,
    lastModifiedOn: null,
    title: "",
  },
  testQuestions: [],
  testQuestionsAnswers: [],
  questionToUpdate: null,
  answerToUpdate: null,
  courseToUpdate: {
    addedOn: null,
    description: "",
    title: "",
    formationId: null,
    id: 0,
    lastModifiedOn: null,
    image: "",
    ressource: "",
    ressourceType: "",
  },
};

export const updateFormationState = createSlice({
  name: "updateFormation",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setPrice: (state, action: PayloadAction<string>) => {
      state.price = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setImage: (state, action: PayloadAction<any>) => {
      state.image = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setCourses: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload;
    },
    setTest: (state, action: PayloadAction<Test>) => {
      state.test = action.payload;
    },
    setCourse: (state, action: PayloadAction<Course>) => {
      state.courseToUpdate = action.payload;
    },
    updateTestTitle: (state, action: PayloadAction<string>) => {
      state.test.title = action.payload;
    },
    updateTestDescription: (state, action: PayloadAction<string>) => {
      state.test.description = action.payload;
    },
    setTestQuestions: (state, action: PayloadAction<Question[]>) => {
      state.testQuestions = action.payload;
    },
    setTestQuestionsAnswers: (state, action: PayloadAction<Answer[]>) => {
      state.testQuestionsAnswers = action.payload;
    },
    setFormationToUpdate: (state, action: PayloadAction<Formation>) => {
      state.formationToUpdate = action.payload;
    },
    setQuestionToUpdate: (state, action: PayloadAction<Question>) => {
      state.questionToUpdate = action.payload;
    },
    setAnswerToUpdate: (state, action: PayloadAction<Answer>) => {
      state.answerToUpdate = action.payload;
    },
    setCourseToUpdate: (state, action: PayloadAction<Course>) => {
      state.courseToUpdate = action.payload;
    },
  },
});

export const {
  setTitle,
  setCourse,
  setQuestionToUpdate,
  setAnswerToUpdate,
  setFormationToUpdate,
  setDescription,
  setImage,
  setPrice,
  setError,
  setLoading,
  setCourses,
  setTest,
  setTestQuestions,
  setTestQuestionsAnswers,
} = updateFormationState.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUpdateFormationData = (state: RootState) =>
  state.updateFormation;
export default updateFormationState.reducer;
